import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Checkbox,
    FormControlLabel,
    Switch,
    Autocomplete,
    TextField,
    CircularProgress,
    Paper,
    Chip,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import {
    Close as CloseIcon,
    Straighten as MeasurementsIcon,
    MonitorWeight as WeightIcon,
    CameraAlt as PhotoIcon,
    Description as FormIcon,
    SelectAll as SelectAllIcon,
    SupervisorAccount as TrainerIcon
} from "@mui/icons-material";
import { getClients } from "../../services/TraineeService";
import { getFormTemplates } from "../../services/FormTemplateService";
import { getTrainers } from "../../services/TrainerService";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import { RTL } from "../../cache/CacheRtl";
import { t as coachT } from "../dashboard/coach/coachTokens";

const CHECK_IN_ITEM_TYPES = [
    {
        type: "BODY_MEASUREMENT",
        label: "מדידות גוף",
        description: "הזן את מדידות הגוף שלך",
        icon: <MeasurementsIcon />,
        color: "#8B5CF6"
    },
    {
        type: "BODY_WEIGHT",
        label: "משקל גוף",
        description: "שקול את עצמך ועדכן",
        icon: <WeightIcon />,
        color: "#3B82F6"
    },
    {
        type: "PROGRESS_PHOTO",
        label: "תמונות התקדמות",
        description: "צלם תמונות התקדמות",
        icon: <PhotoIcon />,
        color: "#10B981"
    },
    {
        type: "FORM",
        label: "מילוי טופס",
        description: "מלא שאלון שנשלח אליך",
        icon: <FormIcon />,
        color: "#F59E0B"
    }
];

const FREQUENCY_OPTIONS = [
    { value: "DAILY", label: "יומי" },
    { value: "WEEKLY", label: "שבועי" },
    { value: "MONTHLY", label: "חודשי" }
];

const DAY_OF_WEEK_OPTIONS = [
    { value: "SUNDAY", label: "א'" },
    { value: "MONDAY", label: "ב'" },
    { value: "TUESDAY", label: "ג'" },
    { value: "WEDNESDAY", label: "ד'" },
    { value: "THURSDAY", label: "ה'" },
    { value: "FRIDAY", label: "ו'" },
    { value: "SATURDAY", label: "ש'" }
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
    const h = String(i).padStart(2, "0");
    return { value: `${h}:00`, label: `${h}:00` };
});

const ScheduledCheckInDialog = ({ open, onClose, onSubmit, editData = null, preSelectedTrainee = null, coach = false }) => {
    const { company } = useCompany();
    const { isAdmin } = useAuth();
    const isEdit = !!editData;

    // Only a company admin (and not when a single trainee is pre-selected) may target a whole
    // trainer's roster. Trainers keep the two original modes (all-mine / specific).
    const canTargetByTrainer = isAdmin() && !preSelectedTrainee;

    const [name, setName] = useState("");
    const [frequency, setFrequency] = useState("WEEKLY");
    const [scheduleDays, setScheduleDays] = useState([]);
    const [daysOfMonth, setDaysOfMonth] = useState([]);
    const [intervalWeeks, setIntervalWeeks] = useState(1);
    const [intervalMonths, setIntervalMonths] = useState(1);
    const [scheduleTime, setScheduleTime] = useState("09:00");

    // Recipient target: "all" (whole company) | "trainer" (all of one trainer's trainees) | "specific".
    const [targetMode, setTargetMode] = useState("all");
    const [selectedTrainees, setSelectedTrainees] = useState([]);
    const [traineesOptions, setTraineesOptions] = useState([]);
    const [traineesLoading, setTraineesLoading] = useState(false);

    const [selectedTrainerId, setSelectedTrainerId] = useState(null);
    const [trainerOptions, setTrainerOptions] = useState([]);
    const [trainersLoading, setTrainersLoading] = useState(false);

    const [selectedItems, setSelectedItems] = useState({});
    const [itemRequired, setItemRequired] = useState({});

    const [formTemplates, setFormTemplates] = useState([]);
    const [formTemplatesLoading, setFormTemplatesLoading] = useState(false);
    const [selectedFormTemplateId, setSelectedFormTemplateId] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            if (editData) {
                setName(editData.name || "");
                setFrequency(editData.frequency || "WEEKLY");
                setScheduleDays(editData.scheduleDays || []);
                setDaysOfMonth(editData.daysOfMonth || []);
                setIntervalWeeks(editData.intervalWeeks || 1);
                setIntervalMonths(editData.intervalMonths || 1);
                setScheduleTime(editData.scheduleTime || "09:00");

                // Restore recipient target: whole company (sendToAll) | one trainer's roster
                // (targetTrainerId) | specific trainees.
                if (editData.sendToAll) {
                    setTargetMode("all");
                    setSelectedTrainerId(null);
                } else if (editData.targetTrainerId) {
                    setTargetMode("trainer");
                    setSelectedTrainerId(editData.targetTrainerId);
                } else {
                    setTargetMode("specific");
                    setSelectedTrainerId(null);
                }

                // Restore items
                const items = {};
                const required = {};
                let formId = null;
                (editData.items || []).forEach(item => {
                    items[item.type] = true;
                    if (item.required) required[item.type] = true;
                    if (item.type === "FORM" && item.formTemplateId) formId = item.formTemplateId;
                });
                setSelectedItems(items);
                setItemRequired(required);
                setSelectedFormTemplateId(formId);

                // Restore trainees
                if (editData.targetTrainees && editData.targetTrainees.length > 0) {
                    setSelectedTrainees(editData.targetTrainees.map(t => ({ id: t.id, name: t.name })));
                } else {
                    setSelectedTrainees([]);
                }
            } else {
                setName("");
                setFrequency("WEEKLY");
                setScheduleDays([]);
                setDaysOfMonth([]);
                setIntervalWeeks(1);
                setIntervalMonths(1);
                setScheduleTime("09:00");
                setTargetMode("all");
                setSelectedTrainerId(null);
                setSelectedTrainees([]);
                setSelectedItems({});
                setItemRequired({});
                setSelectedFormTemplateId(null);
            }
            // From a customer profile the recipients field always shows only the
            // current customer. When editing a schedule that is shared with other
            // customers, those other recipients are preserved on submit (see
            // handleSubmit) — they are intentionally not shown here.
            if (preSelectedTrainee) {
                setTargetMode("specific");
                setSelectedTrainerId(null);
                setSelectedTrainees([preSelectedTrainee]);
            }
            setSubmitting(false);
        }
    }, [open, editData, preSelectedTrainee]);

    useEffect(() => {
        if (open && targetMode === "specific" && !preSelectedTrainee) {
            fetchTrainees();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, targetMode, preSelectedTrainee]);

    useEffect(() => {
        if (open && canTargetByTrainer) {
            fetchTrainers();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, canTargetByTrainer]);

    useEffect(() => {
        if (open && selectedItems["FORM"]) {
            fetchFormTemplates();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedItems["FORM"]]);

    const fetchTrainees = async () => {
        setTraineesLoading(true);
        try {
            const res = await getClients(0, 1000, undefined, 0, 'ALL', 'ACTIVE', company || null);
            const list = res.data?.content || res.data || [];
            setTraineesOptions(list.map(t => ({ id: t.id, name: t.name, photoUrl: t.photoUrl, email: t.email, gender: t.gender })));
        } catch {
            setTraineesOptions([]);
        } finally {
            setTraineesLoading(false);
        }
    };

    const fetchTrainers = async () => {
        setTrainersLoading(true);
        try {
            const res = await getTrainers(company?.id);
            const list = res.data || [];
            setTrainerOptions(list.map(tr => ({ id: tr.id, name: tr.name })));
        } catch {
            setTrainerOptions([]);
        } finally {
            setTrainersLoading(false);
        }
    };

    const fetchFormTemplates = async () => {
        setFormTemplatesLoading(true);
        try {
            const res = await getFormTemplates(0, 100, company?.id);
            const templates = res.data?.content || [];
            setFormTemplates(templates.map(t => ({ id: t.id, name: t.name })));
        } catch {
            setFormTemplates([]);
        } finally {
            setFormTemplatesLoading(false);
        }
    };

    const toggleItem = (type) => {
        setSelectedItems(prev => {
            const next = { ...prev };
            if (next[type]) {
                delete next[type];
                if (type === "FORM") setSelectedFormTemplateId(null);
            } else {
                next[type] = true;
            }
            return next;
        });
    };

    const toggleRequired = (type) => {
        setItemRequired(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const hasSelectedItems = Object.keys(selectedItems).length > 0;
    const hasValidForm = !selectedItems["FORM"] || selectedFormTemplateId;
    const hasTrainees =
        targetMode === "all" ||
        (targetMode === "trainer" && !!selectedTrainerId) ||
        (targetMode === "specific" && selectedTrainees.length > 0);
    const hasValidDay = frequency !== "WEEKLY" || scheduleDays.length > 0;
    const hasValidDaysOfMonth = frequency !== "MONTHLY" || daysOfMonth.length > 0;
    const canSubmit = hasSelectedItems && hasValidForm && hasTrainees && hasValidDay && hasValidDaysOfMonth && !submitting;

    const handleSubmit = async () => {
        setSubmitting(true);
        const items = Object.keys(selectedItems).map(type => ({
            type,
            required: !!itemRequired[type],
            formTemplateId: type === "FORM" ? selectedFormTemplateId : null
        }));

        let traineeIds;
        if (targetMode !== "specific") {
            // Whole company (all) or a whole trainer's roster (trainer) → no explicit trainee list.
            traineeIds = [];
        } else if (preSelectedTrainee && editData) {
            // Editing from a customer profile only shows the current customer, but
            // the schedule may be shared with others — keep all existing recipients.
            const existing = (editData.targetTrainees || []).map(t => t.id);
            traineeIds = existing.includes(preSelectedTrainee.id) ? existing : [...existing, preSelectedTrainee.id];
        } else {
            traineeIds = selectedTrainees.map(t => t.id);
        }

        const data = {
            name: name || null,
            frequency,
            scheduleDays: frequency === "WEEKLY" ? scheduleDays : [],
            daysOfMonth: frequency === "MONTHLY" ? daysOfMonth : [],
            intervalWeeks: frequency === "WEEKLY" ? intervalWeeks : null,
            intervalMonths: frequency === "MONTHLY" ? intervalMonths : null,
            scheduleTime,
            // Whole company → sendToAll; one trainer's roster → trainerId; otherwise explicit ids.
            sendToAll: targetMode === "all",
            trainerId: targetMode === "trainer" ? selectedTrainerId : null,
            traineeIds,
            items
        };

        try {
            await onSubmit(data, editData?.id);
            onClose();
        } catch {
            // error handled by parent
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: coach ? coachT.r : "12px",
                    direction: "rtl"
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "20px 24px",
                backgroundColor: coach ? coachT.bg : undefined,
                borderBottom: coach ? `1px solid ${coachT.border}` : "1px solid #E2E8F0"
            }}>
                <Box>
                    <Typography sx={{
                        fontSize: "18px",
                        fontWeight: coach ? 800 : 600,
                        color: coach ? coachT.text : "#1E293B",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        {isEdit ? "עריכת צ'ק-אין מתוזמן" : "צ'ק-אין מתוזמן חדש"}
                    </Typography>
                    <Typography sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        marginTop: "4px",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        {isEdit ? "ערוך את הגדרות הצ'ק-אין המתוזמן" : "הגדר צ'ק-אין שיישלח באופן אוטומטי"}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: "24px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Name */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "8px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            שם (אופציונלי)
                        </Typography>
                        <TextField
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder='לדוגמה: "בדיקה שבועית"'
                            size="small"
                            fullWidth
                            sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                            }}
                        />
                    </Box>

                    {/* Frequency */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "8px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            תדירות
                        </Typography>
                        <ToggleButtonGroup
                            value={frequency}
                            exclusive
                            onChange={(_, val) => val && setFrequency(val)}
                            sx={{ width: "100%", direction: "ltr" }}
                        >
                            {FREQUENCY_OPTIONS.map(opt => (
                                <ToggleButton
                                    key={opt.value}
                                    value={opt.value}
                                    sx={{
                                        flex: 1,
                                        fontFamily: '"Heebo", "Roboto", sans-serif',
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        textTransform: "none",
                                        border: "1px solid #E2E8F0",
                                        color: "#64748B",
                                        "&.Mui-selected": {
                                            backgroundColor: "rgba(87, 131, 126, 0.1)",
                                            color: "#57837E",
                                            borderColor: "#57837E",
                                            "&:hover": { backgroundColor: "rgba(87, 131, 126, 0.15)" }
                                        }
                                    }}
                                >
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {/* Day of Week + Interval (for WEEKLY) */}
                    {frequency === "WEEKLY" && (
                        <Box>
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                יום בשבוע
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {DAY_OF_WEEK_OPTIONS.map(day => {
                                    const isSelected = scheduleDays.includes(day.value);
                                    return (
                                        <Chip
                                            key={day.value}
                                            label={day.label}
                                            onClick={() => {
                                                setScheduleDays(prev =>
                                                    prev.includes(day.value)
                                                        ? prev.filter(d => d !== day.value)
                                                        : [...prev, day.value]
                                                );
                                            }}
                                            sx={{
                                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                ...(isSelected
                                                    ? {
                                                        backgroundColor: "#57837E",
                                                        color: "#fff",
                                                        "&:hover": { backgroundColor: "#46706B" }
                                                    }
                                                    : {
                                                        backgroundColor: "#F1F5F9",
                                                        color: "#64748B",
                                                        "&:hover": { backgroundColor: "#E2E8F0" }
                                                    })
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "12px" }}>
                                <Typography sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}>
                                    כל
                                </Typography>
                                <TextField
                                    type="number"
                                    value={intervalWeeks}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        if (val >= 1 && val <= 12) setIntervalWeeks(val);
                                    }}
                                    inputProps={{ min: 1, max: 12 }}
                                    size="small"
                                    sx={{
                                        width: "70px",
                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                    }}
                                />
                                <Typography sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}>
                                    {intervalWeeks === 1 ? "שבוע" : "שבועות"}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Days of Month + Interval (for MONTHLY) */}
                    {frequency === "MONTHLY" && (
                        <Box>
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                ימים בחודש
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => {
                                    const isSelected = daysOfMonth.includes(day);
                                    return (
                                        <Chip
                                            key={day}
                                            label={day}
                                            onClick={() => {
                                                setDaysOfMonth(prev =>
                                                    prev.includes(day)
                                                        ? prev.filter(d => d !== day)
                                                        : [...prev, day].sort((a, b) => a - b)
                                                );
                                            }}
                                            size="small"
                                            sx={{
                                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                minWidth: "36px",
                                                ...(isSelected
                                                    ? {
                                                        backgroundColor: "#57837E",
                                                        color: "#fff",
                                                        "&:hover": { backgroundColor: "#46706B" }
                                                    }
                                                    : {
                                                        backgroundColor: "#F1F5F9",
                                                        color: "#64748B",
                                                        "&:hover": { backgroundColor: "#E2E8F0" }
                                                    })
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginTop: "12px" }}>
                                <Typography sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}>
                                    כל
                                </Typography>
                                <TextField
                                    type="number"
                                    value={intervalMonths}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        if (val >= 1 && val <= 12) setIntervalMonths(val);
                                    }}
                                    inputProps={{ min: 1, max: 12 }}
                                    size="small"
                                    sx={{
                                        width: "70px",
                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                    }}
                                />
                                <Typography sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}>
                                    {intervalMonths === 1 ? "חודש" : "חודשים"}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Schedule Time */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "8px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            שעת שליחה
                        </Typography>
                        <Autocomplete
                            options={HOUR_OPTIONS}
                            value={HOUR_OPTIONS.find(h => h.value === scheduleTime) || HOUR_OPTIONS[9]}
                            onChange={(_, val) => val && setScheduleTime(val.value)}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            disableClearable
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={{
                                        width: "120px",
                                        "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* Trainee Selection */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "12px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            שלח אל
                        </Typography>

                        {preSelectedTrainee ? (
                            <Autocomplete
                                multiple
                                disabled
                                options={selectedTrainees}
                                value={selectedTrainees}
                                getOptionLabel={(option) => option.name || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        sx={{
                                            "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                        }}
                                    />
                                )}
                                renderTags={(value, getTagProps) => (
                                    <RTL>
                                        {value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                label={option.name}
                                                size="small"
                                                sx={{
                                                    backgroundColor: "rgba(87, 131, 126, 0.1)",
                                                    color: "#1E293B",
                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                }}
                                            />
                                        ))}
                                    </RTL>
                                )}
                            />
                        ) : (
                        <>
                        <Paper
                            variant="outlined"
                            onClick={() => setTargetMode("all")}
                            sx={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: targetMode === "all" ? "2px solid #57837E" : "1px solid #E2E8F0",
                                backgroundColor: targetMode === "all" ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                "&:hover": { borderColor: "#57837E" }
                            }}
                        >
                            <SelectAllIcon sx={{ color: targetMode === "all" ? "#57837E" : "#94A3B8", fontSize: "20px" }} />
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: targetMode === "all" ? 600 : 400,
                                color: targetMode === "all" ? "#1E293B" : "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                {canTargetByTrainer ? "כל החברה" : "כל המתאמנים שלי"}
                            </Typography>
                        </Paper>

                        {canTargetByTrainer && (
                        <Paper
                            variant="outlined"
                            onClick={() => setTargetMode("trainer")}
                            sx={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: targetMode === "trainer" ? "2px solid #57837E" : "1px solid #E2E8F0",
                                backgroundColor: targetMode === "trainer" ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                "&:hover": { borderColor: "#57837E" }
                            }}
                        >
                            <TrainerIcon sx={{ color: targetMode === "trainer" ? "#57837E" : "#94A3B8", fontSize: "20px" }} />
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: targetMode === "trainer" ? 600 : 400,
                                color: targetMode === "trainer" ? "#1E293B" : "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                כל המתאמנים של מאמן
                            </Typography>
                        </Paper>
                        )}

                        {canTargetByTrainer && targetMode === "trainer" && (
                            <Box sx={{ marginBottom: "8px" }}>
                                <Autocomplete
                                    options={trainerOptions}
                                    loading={trainersLoading}
                                    value={trainerOptions.find(o => o.id === selectedTrainerId) || null}
                                    onChange={(_, newValue) => setSelectedTrainerId(newValue?.id ?? null)}
                                    getOptionLabel={(option) => option?.name || ""}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="בחר מאמן"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {trainersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        )}

                        <Paper
                            variant="outlined"
                            onClick={() => setTargetMode("specific")}
                            sx={{
                                padding: "12px 16px",
                                cursor: "pointer",
                                borderRadius: "8px",
                                border: targetMode === "specific" ? "2px solid #57837E" : "1px solid #E2E8F0",
                                backgroundColor: targetMode === "specific" ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                transition: "all 0.2s",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                "&:hover": { borderColor: "#57837E" }
                            }}
                        >
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: targetMode === "specific" ? 600 : 400,
                                color: targetMode === "specific" ? "#1E293B" : "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                מתאמנים ספציפיים
                            </Typography>
                        </Paper>

                        {targetMode === "specific" && (
                            <Box sx={{ marginTop: "12px" }}>
                                <Autocomplete
                                    multiple
                                    options={traineesOptions}
                                    value={selectedTrainees}
                                    onChange={(_, newValue) => setSelectedTrainees(newValue)}
                                    getOptionLabel={(option) => option.name || ""}
                                    filterOptions={(options, { inputValue }) => {
                                        const q = inputValue.toLowerCase();
                                        return options.filter(o =>
                                            (o.name || "").toLowerCase().includes(q) ||
                                            (o.email || "").toLowerCase().includes(q)
                                        );
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    loading={traineesLoading}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} key={option.id} sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            gap: 1,
                                            direction: "rtl"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                                                    {option.name}
                                                </Typography>
                                                {option.gender && (
                                                    <Chip
                                                        label={option.gender === "FEMALE" ? "נ" : option.gender === "MALE" ? "ז" : "-"}
                                                        size="small"
                                                        sx={{
                                                            height: "18px",
                                                            fontSize: "10px",
                                                            fontWeight: 600,
                                                            ...(option.gender === "FEMALE"
                                                                ? { backgroundColor: "#FDF2F8", color: "#BE185D" }
                                                                : option.gender === "MALE"
                                                                    ? { backgroundColor: "#EFF6FF", color: "#1D4ED8" }
                                                                    : { backgroundColor: "#F3F4F6", color: "#6B7280" }),
                                                            "& .MuiChip-label": { padding: "0 4px" }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {option.email && (
                                                <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                                                    {option.email}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="חפש מתאמנים..."
                                            size="small"
                                            sx={{
                                                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {traineesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) => (
                                        <RTL>
                                            {value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.id}
                                                    label={option.name}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "rgba(87, 131, 126, 0.1)",
                                                        color: "#1E293B",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}
                                                />
                                            ))}
                                        </RTL>
                                    )}
                                />
                            </Box>
                        )}
                        </>
                        )}
                    </Box>

                    {/* Item Selection */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "12px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            פריטים לבדיקה
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            {CHECK_IN_ITEM_TYPES.map((item) => {
                                const isSelected = !!selectedItems[item.type];
                                const isRequired = !!itemRequired[item.type];
                                return (
                                    <Paper
                                        key={item.type}
                                        variant="outlined"
                                        sx={{
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            border: isSelected ? "2px solid #57837E" : "1px solid #E2E8F0",
                                            backgroundColor: isSelected ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                            transition: "all 0.2s"
                                        }}
                                    >
                                        <Box sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => toggleItem(item.type)}
                                                    sx={{
                                                        padding: 0,
                                                        color: "#CBD5E1",
                                                        "&.Mui-checked": { color: "#57837E" }
                                                    }}
                                                />
                                                <Box sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "8px",
                                                    backgroundColor: `${item.color}15`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    "& svg": { color: item.color, fontSize: "20px" }
                                                }}>
                                                    {item.icon}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        color: "#1E293B",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: "12px",
                                                        color: "#64748B",
                                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                                    }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {isSelected && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isRequired}
                                                            onChange={() => toggleRequired(item.type)}
                                                            size="small"
                                                            sx={{
                                                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#DC2626" },
                                                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#DC2626" }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{
                                                            fontSize: "12px",
                                                            color: isRequired ? "#DC2626" : "#94A3B8",
                                                            fontWeight: 600,
                                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                                        }}>
                                                            {isRequired ? "חובה" : "רשות"}
                                                        </Typography>
                                                    }
                                                    labelPlacement="start"
                                                    sx={{ marginRight: 0 }}
                                                />
                                            )}
                                        </Box>

                                        {item.type === "FORM" && isSelected && (
                                            <Box sx={{ marginTop: "12px", paddingRight: "52px" }}>
                                                <Autocomplete
                                                    options={formTemplates}
                                                    value={formTemplates.find(t => t.id === selectedFormTemplateId) || null}
                                                    onChange={(_, newValue) => setSelectedFormTemplateId(newValue?.id || null)}
                                                    getOptionLabel={(option) => option.name || ""}
                                                    loading={formTemplatesLoading}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="בחר טופס..."
                                                            size="small"
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                                                            }}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                endAdornment: (
                                                                    <>
                                                                        {formTemplatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                        {params.InputProps.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                padding: "16px 24px",
                borderTop: "1px solid #E2E8F0",
                gap: 2
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" }
                    }}
                >
                    ביטול
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!canSubmit}
                    sx={{
                        flex: 1,
                        backgroundColor: coach ? coachT.green : "#57837E",
                        borderRadius: coach ? coachT.rs : undefined,
                        boxShadow: coach ? "none" : undefined,
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": { backgroundColor: coach ? coachT.greenDark : "#46706B", boxShadow: coach ? "none" : undefined },
                        "&:disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" }
                    }}
                >
                    {submitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : (isEdit ? "שמור שינויים" : "צור צ'ק-אין מתוזמן")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduledCheckInDialog;
